import { err, ok } from 'neverthrow';
import { EraQL, TC, Ty, Expression, ExprVar } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { v4 } from 'uuid';

/**
 * Given a tool call's formally-specified input/output types,
 * produces a compiled EraQL function of the same input/output types.
 *
 * This item (when wrapped in an CompileOutput), may be added to the
 * parse context of EraQL programs that contain toolcalls, for the
 * purpose of type-checking said workflow programs.
 *
 * @param toolSymbol The symbol that the tool would appear as in the program.
 * @param paramsTy The Era type for the tool's input parameters.
 * @param outputTy The Era type for the tool's output.
 */
export const toolCallSpecialForm = (
  toolSymbol: string,
  paramsTy: Ty.ExtendedAttributeType,
  outputTy: Ty.ExtendedAttributeType
): EraQL.SpecialForm => {
  const sp: EraQL.SpecialForm = (args, ctx, func) => {
    return EraQL.c
      .Args(EraQL.c.Expr)(args, ctx, func)
      .andThen(([argExpr]): EraQL.ParseRes<EraQL.CompileOutput> => {
        if (argExpr.implements(paramsTy)) {
          const outputExprVar: Expression = ExprVar.create({
            name: `${toolSymbol} output ExprVar`,
            scope: `(random uuid) ${v4()}`,
            ty: outputTy,
          });

          return ok({ item: { t: 'expr', expr: outputExprVar } });
        } else {
          const error: EraQL.ParseError = {
            t: 'type-check-error',
            trace: TC.TyStackTrace.fromErr(
              {},
              new TC.InvalidFunctionCall({
                op: toolSymbol,
                recieved: [argExpr.ty],
                allowed: [[[paramsTy], outputTy]],
              })
            ),
          };
          const [_2, range] = Assert.assertExists(args[0]); // by Args combinator success
          return err([[error, range]]);
        }
      });
  };

  return sp;
};
